import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';

const AdminLogin = ({ onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const impersonateParam = searchParams.get('impersonate');
    if (impersonateParam) {
      try {
        const { token, tenant } = JSON.parse(decodeURIComponent(impersonateParam));
        if (token && tenant) {
          onLogin(token, { email: tenant.company_name, role: 'admin', name: `Test: ${tenant.company_name}` });
          toast.success(`Session test ouverte: ${tenant.company_name}`);
          navigate('/admin/dashboard');
        }
      } catch (e) {
        console.error('Impersonate parse error:', e);
      }
    }
    setTimeout(() => setMounted(true), 100);
  }, [searchParams, onLogin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t('common.fill_all_fields')); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, employee } = response.data;
      onLogin(access_token, employee);
      toast.success(t('auth.welcome_user', { name: employee.first_name }));
      const perms = employee.permissions || [];
      const role = employee.role;
      if (role === 'super_admin') {
        navigate('/superadmin/dashboard');
      } else if (perms.includes('admin') || role === 'admin') {
        navigate('/admin/reception-dashboard');
      } else {
        navigate('/admin/reception-dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.login_error'));
    } finally {
      setLoading(false);
    }
  };

  const isMobile = window.innerWidth <= 640;
  const bgImage = isMobile ? '/vortekio-bg-mobile.png' : '/vortekio-bg.png';

  return (
    <>
      <style>{`
        @keyframes float-admin { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
        @keyframes shimmer-admin { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes pulse-red { 0%, 100% { box-shadow: 0 0 20px rgba(220,38,38,0); } 50% { box-shadow: 0 0 35px rgba(220,38,38,0.25); } }
        @keyframes slide-up-a { from { opacity: 0; transform: translateY(25px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gradient-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes dot-pulse-a { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.5); } }
        .admin-overlay { background: linear-gradient(135deg, rgba(15,23,42,0.4), rgba(0,0,0,0.15), rgba(15,23,42,0.5)); background-size: 200% 200%; animation: gradient-bg 8s ease infinite; }
        .admin-float { animation: float-admin 5s ease-in-out infinite; }
        .admin-card-glow { animation: pulse-red 4s ease-in-out infinite; }
        .admin-shimmer { background: linear-gradient(90deg, #94a3b8, #e2e8f0, #fff, #e2e8f0, #94a3b8); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shimmer-admin 4s linear infinite; }
        .admin-slide-1 { animation: slide-up-a 0.6s ease-out 0.1s both; }
        .admin-slide-2 { animation: slide-up-a 0.6s ease-out 0.25s both; }
        .admin-slide-3 { animation: slide-up-a 0.6s ease-out 0.4s both; }
        .admin-dot { animation: dot-pulse-a 2s ease-in-out infinite; }
        .admin-input:focus-within { border-color: rgba(220,38,38,0.5) !important; box-shadow: 0 0 0 2px rgba(220,38,38,0.15), inset 0 0 20px rgba(220,38,38,0.05); }
        .admin-btn { background: linear-gradient(135deg, #dc2626, #ef4444, #b91c1c); background-size: 200% 200%; transition: all 0.3s ease; }
        .admin-btn:hover:not(:disabled) { background-position: 100% 50%; transform: translateY(-1px); box-shadow: 0 8px 25px rgba(220,38,38,0.4); }
      `}</style>

      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundImage: `url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 admin-overlay" />

        {/* Language selector */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSelector />
        </div>

        <div className={`w-full flex flex-col items-center relative z-10 transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          <div className="admin-float mb-1">
            <span
              className="inline-block font-black tracking-tight text-white admin-slide-1"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(3.5rem, 10vw, 7rem)', lineHeight: 1, textShadow: '0 0 40px rgba(255,255,255,0.1)' }}
              data-testid="admin-login-logo"
            >
              vortek<span style={{ position: 'relative', display: 'inline-block' }}>&#305;<span style={{ position: 'absolute', top: '0.05em', left: '50%', transform: 'translateX(-50%)', width: '0.18em', height: '0.18em', borderRadius: '50%', background: '#ef4444', display: 'block' }} /></span>o<span style={{ color: '#ef4444' }}>.</span>
            </span>
          </div>

          <p className="admin-shimmer text-sm sm:text-base font-medium tracking-wider mb-4 admin-slide-1">
            {t('auth.admin_space')}
          </p>

          <div className="flex items-center gap-2 mb-5 admin-slide-2">
            <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-red-500/60" />
            <div className="w-2 h-2 rounded-full bg-red-500 admin-dot" />
            <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-red-500/60" />
          </div>

          <div className="w-full max-w-xs flex flex-col items-center">
            <div
              className="w-full p-5 rounded-2xl space-y-4 admin-card-glow admin-slide-3"
              style={{ background: 'rgba(15, 20, 30, 0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p className="text-center text-slate-400 text-xs tracking-widest uppercase">{t('auth.admin_login')}</p>

              <form onSubmit={handleLogin} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="email" className="flex items-center gap-1.5 text-slate-300 text-xs">
                    <Mail className="w-3.5 h-3.5 text-red-400" /> {t('auth.email')}
                  </Label>
                  <Input
                    id="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.admin_email_placeholder')}
                    className="bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500 rounded-xl h-10 text-sm admin-input transition-all duration-300"
                    data-testid="admin-email-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="password" className="flex items-center gap-1.5 text-slate-300 text-xs">
                    <KeyRound className="w-3.5 h-3.5 text-red-400" /> {t('auth.password')}
                  </Label>
                  <Input
                    id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="--------"
                    className="bg-slate-800/60 border-white/10 text-white placeholder:text-slate-500 rounded-xl h-10 text-sm admin-input transition-all duration-300"
                    data-testid="admin-password-input"
                  />
                </div>

                <Button
                  type="submit" disabled={loading}
                  className="w-full h-10 rounded-xl font-semibold text-sm text-white admin-btn border-0"
                  data-testid="admin-login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('auth.logging_in')}
                    </span>
                  ) : t('auth.login')}
                </Button>
              </form>

              <div className="text-center space-y-1">
                <Button variant="link" onClick={() => navigate('/scan')} className="text-xs text-slate-500 hover:text-white transition-colors duration-300" data-testid="back-to-scan-link">
                  {t('auth.back_to_timeclock')}
                </Button>
                <div>
                  <Button variant="link" onClick={() => navigate('/presentation')} className="text-xs text-red-400 hover:text-red-300 font-semibold transition-colors duration-300" data-testid="discover-vortekio-link">
                    {t('auth.discover_vortekio')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
